import React, { useRef, useState } from "react";
import { FileAnswerFormat } from "../../../../types/Survey";
import "text-security/text-security.css"
import { SurveyQuestionProps } from "../../Components/SurveyQuestion/SurveyQuestion";
import SurveyFormItem from "../../Components/SurveyFormItem/SurveyFormItem";
import useTranslate from "../../../../hooks/useTranslate";
import { FileResultModel } from "../../../../types/SurveyResult";
import UnstyledButton from "../../../presentational/UnstyledButton/UnstyledButton";

const knownAudioFormats = [
    '3gpp',
    '3gpp2',
    'aiff',
    'x-aiff',
    'amr',
    'mp3',
    'mpeg3',
    'x-mp3',
    'x-mpeg3',
    'mp4',
    'mpeg',
    'x-mpeg',
    'wav',
    'x-wav',
    'x-m4a',
    'x-m4b',
    'x-m4p'
];

export default function (props: SurveyQuestionProps) {
    let [validationError, setValidationError] = useState<string | undefined>(undefined);
    const inputRef = useRef<HTMLInputElement>(null);
    let answerFormat = props.answerFormat as FileAnswerFormat;
    let defaultPlaceholder = useTranslate(["default-file-placeholder"])["default-file-placeholder"];
    let placeholder = props.placeholder || defaultPlaceholder;
    let currentResult = props.result as FileResultModel;

    function answerChanged(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files || e.target.files.length === 0) {
            props.onResultChange({ ...props.result, File: undefined, FileName: undefined, ContentType: undefined, StorageLocation: "UploadedFiles", Modified: true } as FileResultModel);
            return;
        }

        let file = e.target.files[0];
        let contentType = file.type;
        let extension = file.name.split('.').pop();
        if (contentType === "image/heic" || (extension && extension.toLowerCase() == "heic")) {
            setValidationError("HEIC file format is not supported. Supported image formats include JPEG and PNG.");
            props.onResultChange({ ...props.result, File: file, FileName: file.name, ContentType: contentType, StorageLocation: "UploadedFiles", Modified: true, invalid: true } as FileResultModel);
            return;
        }

        let targetFileName = answerFormat.fileName ? `${answerFormat.fileName}.${extension}` : file.name;
        if (targetFileName && !/^[a-zA-Z0-9\-_\.\'\(\) ]+$/.test(targetFileName)) {
            // Remove invalid characters
            targetFileName = targetFileName.replace(/[^a-zA-Z0-9\-_\.\'\(\) ]/g, "");
            if (targetFileName === "" || targetFileName.startsWith(".")) {
                props.onResultChange({ ...props.result, File: file, FileName: targetFileName, ContentType: contentType, StorageLocation: "UploadedFiles", Modified: true, invalid: true } as FileResultModel);
                setValidationError("Filename contains only invalid characters. Please rename the file and try again.");
                return;
            }
        }

        if (targetFileName != file.name) {
            file = new File([file!], targetFileName, { type: file!.type });
        }

        setValidationError(undefined);
        props.onResultChange({ ...props.result, File: file, FileName: targetFileName, ContentType: contentType, StorageLocation: "UploadedFiles", Modified: true } as FileResultModel);
    }

    const addExplicitAudioFormats = (accept: string) => {
        return accept.replace('audio/*', `audio/*,${knownAudioFormats.map(format => `audio/${format}`).join(',')}`);
    }

    let defaultAccept = "image/*,video/*,audio/*,application/pdf";
    return <SurveyFormItem className="text-question"
        bottomBorder
        topBorder
        label={props.label}
        labelFor={props.inputIdForLabel}
        labelPosition={props.labelPosition}
        variant={props.variant}
        validationError={validationError}
        useFieldsetFormat={props.useFieldsetFormat}
        index={props.index}>
        <UnstyledButton
            aria-label={placeholder}
            className={currentResult.FileName ? "survey-input-value-button survey-input-value-button-v2" : "survey-input-placeholder-button survey-input-placeholder-button-v2"}
            onClick={() => { inputRef.current?.click(); }}>
            {currentResult.FileName || placeholder}
        </UnstyledButton>
        <input
            id={props.inputIdForLabel}
            type="file"
            onChange={(e) => answerChanged(e)}
            capture={answerFormat.capture ? answerFormat.capture : undefined}
            accept={addExplicitAudioFormats(answerFormat.accept || defaultAccept)}
            ref={inputRef}
            style={{ display: "none" }}
        />
    </SurveyFormItem>
}